import React, { useState, useContext, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import debounce from 'lodash.debounce';
import AppContext from '../AppContext/AppContext';
import { useNavigate } from 'react-router-dom';
import Iconify from '../Iconify/Iconify';
import { IconButton, Tooltip, Stack } from '@mui/material';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import './project.scss';
import { Config } from '../../Config';
import Modal from '../Modal/Modal';

function Project() {
  const { projectId } = useParams();
  const [pages, setPages] = useState<any>([]);
  const [pageEdited, setPageEdited] = useState({ _id: '', name: '' });
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [fetching, setFetching] = useState<string>('');
  const [openModalConfirm, setOpenModalConfirm] = useState(false);
  const [pageDeleteId, setPageDeleteId] = useState('');

  const getAllPages = useCallback(async () => {
    try {
      const option = {
        method: 'GET',
        url: Config.API_URL+'/v1/pages/project/' + projectId,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      };
      const res = await axios(option);
      setPages(res.data.data.pages);
    } catch (error: any) {
      setError(error.response.data.error);
      setOpenSnackbar(true);
    }
  }, [projectId]);

  useEffect(() => {
    getAllPages();
  }, [getAllPages]);

  const updatePage = useCallback(
    debounce(async () => {
      if (pageEdited._id === '') return;
      try {
        const option = {
          method: 'PUT',
          url: Config.API_URL+'/v1/pages/' + pageEdited._id,
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          data: pageEdited,
        };
        await axios(option);
      } catch (error: any) {
        setError(error.response.data.error);
        setOpenSnackbar(true);
      }
    }, 300),
    [pageEdited]
  );

  const handleChangePage = (e: any, id: any) => {
    const newPages = pages.map((page: any) => {
      if (page._id === id) {
        page.name = e.target.value;
        setPageEdited(page);
      }
      return page;
    });
    updatePage();
    setPages(newPages);
  };

  const handleClickPage = (id: any) => {
    navigate(`/projects/${projectId}/${id}`);
  };

  const handleDeletePage = async (id: any) => {
    try {
      const option = {
        method: 'DELETE',
        url: Config.API_URL+'/v1/pages/' + id,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      };
      await axios(option);
      const newPages = pages.filter((page: any) => page._id !== id);
      setPages(newPages);
    } catch (error: any) {
      setError(error.response.data.error);
      setOpenSnackbar(true);
    }
  };

  return (
    <div className="project project-page">
      <div className="project-page__toolbar">
        <div className="project-page__toolbar-left">
          <Tooltip title="All projects" arrow>
            <IconButton
              onClick={() => {
                navigate('/projects/');
              }}
              className="back-icon project-page__back"
              aria-label="Back to all projects"
            >
              <Iconify icon="material-symbols:arrow-back-sharp" />
            </IconButton>
          </Tooltip>
        </div>
        <div className="project-page__toolbar-right">
          <div className="project-page__notice">
            <img src={Config.PUBLIC_URL + '/chrome.svg'} alt="" />
            <span>
              Add pages with the{' '}
              <a
                href="https://chrome.google.com/webstore/detail/clonewebx-softliteio/kejifndpehkapckhogiecndmachaeilp"
                target="_blank"
                rel="noreferrer"
              >
                ClonewebX Chrome extension
              </a>
              .
            </span>
          </div>
        </div>
      </div>

      <div className="pages project-page__grid">
        {pages.map((page: any) => (
          <div
            className={
              fetching === page._id
                ? 'pages__item project-page__card fetching'
                : 'pages__item project-page__card'
            }
            data-key={page._id}
            key={page._id}
          >
            <div
              className="pages__item-image"
              style={{
                backgroundImage: page.thumbnailUrl
                  ? 'url(' + Config.PUBLIC_URL + '/resources/' + page.thumbnailUrl + ')'
                  : undefined,
                backgroundColor: page.thumbnailUrl ? undefined : '#e8ecf4',
              }}
            >
              <div className="pages__item-center">
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClickPage(page._id);
                  }}
                  variant="contained"
                >
                  View page
                </Button>
              </div>
              <div className="pages__item-tools">
                <div className="pages__item-tool">
                  <Tooltip title="Delete" arrow>
                    <IconButton
                      className="project-page__delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPageDeleteId(page._id);
                        setOpenModalConfirm(true);
                      }}
                      size="small"
                    >
                      <Iconify icon="ph:trash" />
                    </IconButton>
                  </Tooltip>
                </div>
              </div>
            </div>
            <div className="pages__item-name">
              <input
                type="text"
                value={page.name}
                title={page.name}
                onChange={(e) => handleChangePage(e, page._id)}
              />
            </div>
          </div>
        ))}
      </div>

      {pages.length === 0 && (
        <div className="steps project-page__steps">
          <div className="steps__heading">
            <h2 className="steps__heading-title">How to clone a webpage</h2>
          </div>
          <div className="steps__list">
            <div className="steps__item">
              <div className="steps__item-title">
                <div className="steps__item-title-number">1</div>
                <div className="steps__item-title-text">Install ClonewebX Chrome extension</div>
              </div>
              <div className="steps__item-desc">
                <a
                  href="https://chrome.google.com/webstore/detail/clonewebx-softliteio/kejifndpehkapckhogiecndmachaeilp"
                  target="_blank"
                  rel="noreferrer"
                >
                  Click here to install the extension
                </a>
                .
              </div>
            </div>
            <div className="steps__item">
              <div className="steps__item-title">
                <div className="steps__item-title-number">2</div>
                <div className="steps__item-title-text">Open your target webpage</div>
              </div>
              <div className="steps__item-desc">
                Scroll through the page so images and lazy content fully load.
              </div>
            </div>
            <div className="steps__item">
              <div className="steps__item-title">
                <div className="steps__item-title-number">3</div>
                <div className="steps__item-title-text">Clone the page</div>
              </div>
              <div className="steps__item-desc">
                Click the ClonewebX icon, and then click &quot;Add page&quot;. Your pages
                will show up here.
              </div>
            </div>
          </div>
        </div>
      )}

      <Snackbar
        open={openSnackbar}
        onClose={() => setOpenSnackbar(false)}
        message={error}
        autoHideDuration={3000}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      />

      {openModalConfirm && (
        <Modal
          modalTitle={'Confirm delete page'}
          setOpenModal={setOpenModalConfirm}
          openModal={openModalConfirm}
          modalContent={
            <div className="modal__content">
              <div className="modal__content-text">
                Are you sure you want to delete this page?
              </div>
              <br />
              <Stack direction="row" spacing={2} color="#3c6df1">
                <Button
                  variant="outlined"
                  size="large"
                  fullWidth
                  onClick={() => {
                    setOpenModalConfirm(false);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={() => {
                    handleDeletePage(pageDeleteId);
                    setOpenModalConfirm(false);
                  }}
                >
                  Delete
                </Button>
              </Stack>
            </div>
          }
        />
      )}
    </div>
  );
}

export default Project;
